import {
  Alert,
  AlertDescription,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@tinct/web-components';
import { SsoWorkspaceStatus } from '@tinct/core-api-models';
import { InfoIcon } from 'lucide-react';

type StateCopy = {
  label: string;
  variant: 'secondary' | 'success' | 'important';
  description: string;
};

/** The one word and one sentence a member gets per state; nothing about the identity provider. */
export const SSO_STATE_COPY: Record<SsoWorkspaceStatus, StateCopy> = {
  none: {
    label: 'Not set up',
    variant: 'secondary',
    description: 'No identity provider is connected to this workspace.',
  },
  draft: {
    label: 'Being set up',
    variant: 'secondary',
    description:
      'An identity provider is being connected. Signing in through it is not available yet.',
  },
  tested: {
    label: 'Tested',
    variant: 'secondary',
    description:
      'The identity provider answered a test sign-in. It is not switched on for members yet.',
  },
  enabled: {
    label: 'Enabled',
    variant: 'success',
    description:
      "Members on the workspace's verified domains are signed in through the identity provider by default. Their other ways of signing in still work.",
  },
  enforced: {
    label: 'Required',
    variant: 'important',
    description:
      "Members on the workspace's verified domains must sign in through the identity provider.",
  },
};

export type SsoAdminOnlyCardProps = {
  /** `organization.ssoStatus`; `null` while unknown. */
  status: SsoWorkspaceStatus | null;
};

/**
 * What an editor or a viewer sees of single sign-on: where the workspace stands, and nothing
 * else. The API reserves every read of the connection for the workspace's admins (403), so a
 * disabled copy of the cards would only ever be empty; the state comes off the organization.
 */
export function SsoAdminOnlyCard({ status }: SsoAdminOnlyCardProps) {
  const copy = status ? SSO_STATE_COPY[status] : null;
  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <CardTitle className="text-xl">Single sign-on</CardTitle>
        <CardDescription>
          This workspace can sign its members in through its own identity
          provider.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {copy && (
          <div className="flex items-start gap-3">
            <Badge variant={copy.variant} className="mt-0.5 shrink-0">
              {copy.label}
            </Badge>
            <span className="text-sm text-muted-foreground">{copy.description}</span>
          </div>
        )}
        <Alert>
          <InfoIcon />
          <AlertDescription>
            Only workspace admins can change this setting.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
